import { useState, useEffect, useCallback, useMemo } from "react";
import { contactsService } from "@/api/services/contacts.service";
export const NO_COMPANY_SENTINEL = "__NO_COMPANY__";
const TRADE_CATEGORY_FIELD_KEY = "contact.trade_category";
function normalizeCompany(companyName) {
    const trimmed = companyName != null && typeof companyName === "string"
        ? companyName.trim()
        : "";
    return trimmed === "" ? null : trimmed;
}
export function useGhlTradeContacts(subcontractorGroup, tradeCategory, companyFilter) {
    const [allOptions, setAllOptions] = useState([]);
    const [companyOptions, setCompanyOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const fetchContacts = useCallback(async (signal) => {
        if (!subcontractorGroup.trim() || !tradeCategory.trim()) {
            setAllOptions([]);
            setCompanyOptions([]);
            setLoading(false);
            setError(null);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const response = await contactsService.getGhlContacts(subcontractorGroup, tradeCategory, { signal });
            const raw = response.data ?? [];
            const byTradeCategory = raw.filter((item) => item.fieldKey === TRADE_CATEGORY_FIELD_KEY &&
                item.name === tradeCategory);
            const seen = new Set();
            const optionsList = [];
            const companySet = new Set();
            let hasNoCompany = false;
            for (const item of byTradeCategory) {
                if (!item.contactId || seen.has(item.contactId))
                    continue;
                seen.add(item.contactId);
                const company = normalizeCompany(item.companyName);
                if (company === null) {
                    hasNoCompany = true;
                }
                else {
                    companySet.add(company);
                }
                optionsList.push({
                    id: item.contactId,
                    name: item.contactName || item.contactId,
                    companyName: company ?? undefined,
                });
            }
            const companyOptionsList = [];
            if (hasNoCompany) {
                companyOptionsList.push({
                    value: NO_COMPANY_SENTINEL,
                    label: "No company",
                });
            }
            Array.from(companySet)
                .sort((a, b) => a.localeCompare(b))
                .forEach((name) => {
                companyOptionsList.push({ value: name, label: name });
            });
            setAllOptions(optionsList);
            setCompanyOptions(companyOptionsList);
            setLoading(false);
            setError(null);
        }
        catch (err) {
            if (err instanceof Error && err.name === "AbortError")
                return;
            const message = err instanceof Error ? err.message : "Failed to load contacts";
            setAllOptions([]);
            setCompanyOptions([]);
            setLoading(false);
            setError(message);
        }
    }, [subcontractorGroup, tradeCategory]);
    useEffect(() => {
        const controller = new AbortController();
        fetchContacts(controller.signal);
        return () => controller.abort();
    }, [fetchContacts]);
    const options = useMemo(() => {
        if (companyFilter == null || companyFilter === "") {
            return [];
        }
        if (companyFilter === NO_COMPANY_SENTINEL) {
            return allOptions.filter((o) => o.companyName == null || o.companyName === "");
        }
        return allOptions.filter((o) => o.companyName === companyFilter);
    }, [allOptions, companyFilter]);
    return {
        options,
        allOptions,
        companyOptions,
        loading,
        error,
    };
}
